/* eslint-disable no-restricted-imports */
/**
 * Date/time utility wrapper around Luxon.
 * ALL public APIs accept and return strings or primitives — no Luxon types are leaked.
 * This is the ONLY file that may import from 'luxon'.
 */
import { DateTime, Duration, type DurationLikeObject } from 'luxon';

/**
 * Parses a server timestamp as an instant: a zone-less string (Python's bare `.isoformat()` of a
 * UTC column) reads as UTC, not the viewer's local time; a string with `Z` / an offset keeps it.
 * Used by every elapsed / duration helper so "running for" never gains the viewer's UTC offset.
 */
function fromServerIso(iso: string): DateTime {
  return DateTime.fromISO(iso, { zone: 'utc' });
}

/**
 * Format an ISO date string as a human-readable date.
 * @example formatDate('2025-01-15T10:30:00Z', 'en-US') => 'January 15, 2025'
 */
export function formatDate(iso: string, locale = 'en-US'): string {
  return DateTime.fromISO(iso, { locale }).toLocaleString(DateTime.DATE_FULL);
}

/**
 * Format an ISO date string as a compact human-readable date (short month, no weekday).
 * Used where `formatDate`'s full month name ("January 15, 2025") is too wide — e.g. a table
 * cell or KPI caption that needs to stay on one line.
 * @example formatDateMedium('2025-01-15T10:30:00Z', 'en-US') => 'Jan 15, 2025'
 */
export function formatDateMedium(iso: string, locale = 'en-US'): string {
  return DateTime.fromISO(iso, { locale }).toLocaleString(DateTime.DATE_MED);
}

/**
 * Format an ISO date string as a human-readable date + time.
 * @example formatDateTime('2025-01-15T10:30:00Z', 'en-US') => 'January 15, 2025, 10:30 AM'
 */
export function formatDateTime(iso: string, locale = 'en-US'): string {
  return DateTime.fromISO(iso, { locale }).toLocaleString(DateTime.DATETIME_MED);
}

/**
 * Same output as `formatDateTime`, but reads a zone-less timestamp as UTC rather
 * than as the viewer's local time.
 *
 * The curation service stores `timestamp without time zone` columns on a UTC
 * server and serialises them with a bare `.isoformat()` — no `Z`, no offset. Luxon
 * assumes local for such a string, which silently shifts every value by the
 * viewer's offset. Timestamps that DO carry a zone are unaffected: the offset in
 * the string still wins, and the result is converted to local either way.
 */
export function formatUtcNaiveDateTime(iso: string, locale = 'en-US'): string {
  return DateTime.fromISO(iso, { locale, zone: 'utc' })
    .toLocal()
    .toLocaleString(DateTime.DATETIME_MED);
}

/**
 * The viewer's timezone abbreviation for a given instant (e.g. `GMT+5:30`, `PST`).
 * Rendered separately from the date-time so it can be styled on its own.
 * @example getTimeZoneAbbrev('2025-01-15T10:30:00Z') => 'GMT+5:30'
 */
export function getTimeZoneAbbrev(iso: string, locale = 'en-US'): string {
  return DateTime.fromISO(iso, { locale }).toFormat('ZZZZ');
}

/**
 * Weekday + day + month, with the year only when `withYear` is set. Built for
 * lists of upcoming dates, where repeating the year on every row is noise.
 * @example formatWeekdayDate('2026-08-03T21:32:00Z') => 'Mon, Aug 3'
 * @example formatWeekdayDate('2027-01-04T21:32:00Z', true) => 'Mon, Jan 4, 2027'
 */
export function formatWeekdayDate(iso: string, withYear = false, locale = 'en-US'): string {
  return DateTime.fromISO(iso, { locale }).toFormat(withYear ? 'ccc, LLL d, yyyy' : 'ccc, LLL d');
}

/**
 * Just the clock time, for lists that show the date and time in separate columns.
 * @example formatTimeOfDay('2026-08-03T21:32:00Z') => '9:32 PM'
 */
export function formatTimeOfDay(iso: string, locale = 'en-US'): string {
  return DateTime.fromISO(iso, { locale }).toFormat('h:mm a');
}

/**
 * `formatTimeOfDay` for zone-less server timestamps that are really UTC (run `startedAt` /
 * `finishedAt`), shown in the viewer's local clock.
 * @example formatUtcNaiveTimeOfDay('2026-08-03T16:02:00') => '9:32 PM' (in IST)
 */
export function formatUtcNaiveTimeOfDay(iso: string, locale = 'en-US'): string {
  return fromServerIso(iso).setLocale(locale).toLocal().toFormat('h:mm a');
}

/** Calendar year of an ISO timestamp, for deciding whether a list must show years. */
export function getYear(iso: string): number {
  return DateTime.fromISO(iso).year;
}

/**
 * Format an ISO date string as a relative time string.
 * @example formatRelative('2025-01-15T10:30:00Z') => '2 hours ago'
 */
export function formatRelative(iso: string, locale = 'en-US'): string {
  return DateTime.fromISO(iso, { locale }).toRelative() ?? iso;
}

/**
 * `formatRelative` for zone-less timestamps that are really UTC (see
 * `formatUtcNaiveDateTime`) — reading them as local would skew "x ago" by the offset.
 * @example formatUtcNaiveRelative('2025-01-15T10:30:00') => '2 hours ago'
 */
export function formatUtcNaiveRelative(iso: string, locale = 'en-US'): string {
  return DateTime.fromISO(iso, { locale, zone: 'utc' }).toRelative() ?? iso;
}

/**
 * Compact, fixed duration between two ISO timestamps — shows the two largest non-zero units
 * (seconds only below 1h). Unlike `formatRelative` it's derived from two fixed instants, so it
 * never drifts on re-render: used for terminal pipeline stages that must report how long a stage
 * took, not a live "time ago". Returns '' for missing/invalid/negative input so callers can fall
 * back to a static label.
 * @example formatDurationBetween('…T10:00:00Z', '…T10:02:15Z') => '2m 15s'
 */
export function formatDurationBetween(startIso: string, endIso: string): string {
  // Zone-less server timestamps read as UTC (see `fromServerIso`) — an instant diff either way.
  const ms = fromServerIso(endIso).diff(fromServerIso(startIso)).toMillis();
  return formatDurationMs(ms);
}

/**
 * Compact duration from a raw millisecond count — the same two-largest-units
 * format as `formatDurationBetween`, for APIs that hand back a duration long
 * (`durationMs`, `stalledForMillis`, `etaMillis`) instead of two instants.
 * Returns '' for missing/invalid/negative input so callers can fall back.
 * @example formatDurationMs(135000) => '2m 15s'
 */
export function formatDurationMs(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms) || ms < 0) return '';
  const d = Duration.fromMillis(ms).shiftTo('days', 'hours', 'minutes', 'seconds');
  const parts: string[] = [];
  const push = (value: number, unit: string): void => {
    const whole = Math.floor(value);
    if (whole) parts.push(`${whole.toString()}${unit}`);
  };
  push(d.days, 'd');
  push(d.hours, 'h');
  push(d.minutes, 'm');
  // Seconds only matter for sub-hour durations; longer spans round to m/h/d.
  if (ms < 3_600_000) push(d.seconds, 's');
  if (parts.length === 0) return '<1s';
  return parts.slice(0, 2).join(' ');
}

/**
 * Signed milliseconds from `startIso` to `endIso` — positions bars on
 * millisecond-scale timelines (Temporal event history), where whole-unit
 * helpers are too coarse. NaN-safe: invalid input yields 0.
 */
export function diffInMs(startIso: string, endIso: string): number {
  const ms = fromServerIso(endIso).diff(fromServerIso(startIso)).toMillis();
  return Number.isFinite(ms) ? ms : 0;
}

/**
 * Epoch milliseconds of a server timestamp, reading a zone-less string as UTC (unlike
 * `Date.parse`, which reads it as the viewer's local time). `NaN` when unparseable.
 * @example serverIsoToMillis('2026-10-26T16:15:00') === Date.parse('2026-10-26T16:15:00Z')
 */
export function serverIsoToMillis(iso: string): number {
  const dt = fromServerIso(iso);
  return dt.isValid ? dt.toMillis() : Number.NaN;
}

/**
 * Duration with sub-second precision for short spans (`84ms`, `2s 84ms`),
 * falling back to `formatDurationMs` from a minute up. Used where activities
 * routinely finish in milliseconds and "<1s" would hide the difference.
 */
export function formatPreciseDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms) || ms < 0) return '';
  const whole = Math.round(ms);
  if (whole < 1000) return `${whole.toString()}ms`;
  if (whole < 60_000) {
    const rest = whole % 1000;
    const secs = Math.floor(whole / 1000).toString();
    return rest ? `${secs}s ${rest.toString()}ms` : `${secs}s`;
  }
  return formatDurationMs(whole);
}

/**
 * UTC clock time with centiseconds (`10:48:45.14`) — for timelines whose events
 * all fall on one day, where the date would only repeat. '' for invalid input.
 */
export function formatUtcTimeOfDayPrecise(iso: string): string {
  const dt = DateTime.fromISO(iso, { zone: 'utc' });
  return dt.isValid ? `${dt.toFormat('HH:mm:ss')}.${dt.toFormat('SSS').slice(0, 2)}` : '';
}

/**
 * UTC timestamp with centiseconds (`30 Sept 2026, 10:48:45.14 UTC`) — event
 * history rows can share a second, so the fraction is what orders them visually.
 */
export function formatUtcTimestampPrecise(iso: string, locale = 'en-GB'): string {
  const dt = DateTime.fromISO(iso, { zone: 'utc', locale });
  return dt.isValid
    ? `${dt.toFormat('d LLL yyyy, HH:mm:ss')}.${dt.toFormat('SSS').slice(0, 2)} UTC`
    : iso;
}

/**
 * Returns the current time as an ISO 8601 string.
 */
export function nowISO(): string {
  return DateTime.now().toISO();
}

/**
 * Current UTC moment in the compact forms SFTP output file names render:
 * `date` → `YYYYMMDD`, `timestamp` → `YYYYMMDDTHHMMSSZ` (backend `{date}` /
 * `{timestamp}` placeholders). Used for an approximate preview before delivery.
 */
export function formatUtcNowCompact(kind: 'date' | 'timestamp'): string {
  const now = DateTime.utc();
  return kind === 'date' ? now.toFormat('yyyyLLdd') : now.toFormat("yyyyLLdd'T'HHmmss'Z'");
}

/**
 * Returns today's calendar date in `YYYY-MM-DD` (ISO date-only, no time).
 * Used for backend fields that expect a date-only string (e.g. `ingestionDate`).
 */
export function todayIsoDate(): string {
  return DateTime.now().toISODate();
}

/**
 * Convert a Date object or date string to an ISO 8601 string.
 */
export function toISO(date: Date | string): string {
  if (typeof date === 'string') {
    return DateTime.fromISO(date).toISO() ?? date;
  }
  return DateTime.fromJSDate(date).toISO() ?? date.toISOString();
}

/**
 * Date-only (`YYYY-MM-DD`) for a JS Date — used to send a date-picker value as
 * a backend date param (e.g. analytics sync `start_date`/`end_date`).
 */
export function toIsoDate(date: Date): string {
  return DateTime.fromJSDate(date).toISODate() ?? '';
}

/**
 * Inverse of `toIsoDate`: a `YYYY-MM-DD` string as a local-midnight JS Date for
 * a date picker's `value`. `undefined` for an empty or unparseable string so the
 * picker shows its placeholder instead of an "Invalid Date".
 */
export function fromIsoDate(iso: string): Date | undefined {
  if (!iso) return undefined;
  const dt = DateTime.fromISO(iso);
  return dt.isValid ? dt.toJSDate() : undefined;
}

/** Instants at or before this are epoch / zero placeholders, never real observations. */
const EPOCH_SENTINEL_CUTOFF_MS = Date.UTC(1971, 0, 1);

/**
 * `true` for a real, parseable instant; `false` for null/blank, unparseable, or an
 * epoch/zero sentinel (`1970-01-01`) a store wrote for "never" — so it never renders
 * as "January 1, 1970".
 */
export function isRealTimestamp(iso: string | null | undefined): iso is string {
  if (!iso) return false;
  const ms = Date.parse(iso);
  return !Number.isNaN(ms) && ms > EPOCH_SENTINEL_CUTOFF_MS;
}

/**
 * Add a number of days to an ISO date string. Returns a new ISO string.
 */
export function addDays(iso: string, days: number): string {
  const duration: DurationLikeObject = { days };
  return DateTime.fromISO(iso).plus(duration).toISO() ?? iso;
}

/**
 * Add a millisecond offset to an ISO instant — e.g. a batch's start instant plus its stages'
 * estimated elapsed, to get a clock time for "Your review ≈ <time>" rather than a duration.
 */
export function addMillis(iso: string, ms: number): string {
  return DateTime.fromISO(iso).plus({ milliseconds: ms }).toISO() ?? iso;
}

/**
 * ISO 8601 timestamp for `days` ago, in **UTC** (always `Z`-suffixed). Backend
 * `Instant` query params (e.g. the experiments-list `createdAfter`) parse via
 * `ISO_INSTANT`, which expects the `Z` designator — so emit UTC, not a local offset.
 */
export function daysAgoISO(days: number): string {
  // `now().minus()` is always a valid DateTime, so `toISO()` is non-null here
  // (mirrors nowISO()); no `?? fallback` needed (and it would be flagged unused).
  return DateTime.now().minus({ days }).toUTC().toISO();
}

/**
 * Every UTC calendar day (`YYYY-MM-DD`) from `sinceIso`'s day up to `untilIso`, so a per-day
 * series can show the quiet days as 0 rather than skip them.
 * @example utcDatesBetween('2026-10-01T09:00:00Z', '2026-10-03T08:00:00Z') => ['2026-10-01', '2026-10-02', '2026-10-03']
 */
export function utcDatesBetween(sinceIso: string, untilIso: string): string[] {
  const end = DateTime.fromISO(untilIso).toUTC();
  const dates: string[] = [];
  // Capped at a year and a day, the longest window the overview reads.
  for (
    let day = DateTime.fromISO(sinceIso).toUTC().startOf('day');
    day < end && dates.length < 367;
    day = day.plus({ days: 1 })
  ) {
    dates.push(day.toISODate() ?? '');
  }
  return dates;
}

/**
 * The `[since, until)` window of a `days`-long period, as UTC ISO strings. `periodsBack` 0 is the
 * period ending at `anchorIso`; 1 the one before it (for "change vs the previous period").
 * @example periodWindowISO('2026-10-14T12:00:00Z', 30, 1) => { since: '…08-15…', until: '…09-14…' }
 */
export function periodWindowISO(
  anchorIso: string,
  days: number,
  periodsBack = 0,
): { since: string; until: string } {
  const until = DateTime.fromISO(anchorIso)
    .toUTC()
    .minus({ days: days * periodsBack });
  return { since: until.minus({ days }).toISO() ?? anchorIso, until: until.toISO() ?? anchorIso };
}

/**
 * Date-only (`YYYY-MM-DD`) string for `days` ago. Used for backend `start_date`
 * bounds that expect a bare calendar date, not a full timestamp.
 */
export function isoDateDaysAgo(days: number): string {
  return DateTime.now().minus({ days }).toISODate();
}

/**
 * Time-only label (e.g. `7:53 AM`). Used where the date is already shown as a
 * section header and only the run time needs to appear per row.
 */
export function formatTime(iso: string, locale = 'en-US'): string {
  return DateTime.fromISO(iso, { locale }).toLocaleString(DateTime.TIME_SIMPLE);
}

/**
 * Calculate the difference in days between two ISO date strings.
 */
export function diffInDays(startIso: string, endIso: string): number {
  const start = DateTime.fromISO(startIso);
  const end = DateTime.fromISO(endIso);
  const diff = end.diff(start, 'days').days;
  return Math.round(diff);
}

/**
 * Whole hours elapsed between two ISO timestamps, floored. Unlike `diffInDays` this does not
 * round to nearest — a threshold check ("has it been idle 6 hours?") must not report 6 when
 * only 5h31m has passed.
 */
export function diffInHours(startIso: string, endIso: string): number {
  const diff = fromServerIso(endIso).diff(fromServerIso(startIso), 'hours').hours;
  return Number.isFinite(diff) ? Math.floor(diff) : 0;
}

/**
 * Check if two ISO date strings represent the same calendar day.
 */
export function isSameDay(isoA: string, isoB: string): boolean {
  return DateTime.fromISO(isoA).hasSame(DateTime.fromISO(isoB), 'day');
}

/**
 * True when the given ISO timestamp is later than the current time.
 */
export function isFutureIso(iso: string): boolean {
  return DateTime.fromISO(iso) > DateTime.now();
}

/**
 * True when the given ISO timestamp is earlier than the current time.
 */
export function isPastIso(iso: string): boolean {
  return DateTime.fromISO(iso) < DateTime.now();
}

/**
 * True when ISO timestamp `a` is strictly later than `b`. Zone-less strings read as UTC, as in
 * `formatUtcNaiveDateTime`; an unparseable value compares false rather than throwing.
 */
export function isAfterIso(a: string, b: string): boolean {
  return fromServerIso(a) > fromServerIso(b);
}

/**
 * The UTC calendar day (`YYYY-MM-DD`) an instant falls on — for bucketing timestamps per day the
 * same way `utcDatesBetween` lists the days. '' for invalid input.
 * @example utcDateKey('2026-10-14T23:30:00-02:00') => '2026-10-15'
 */
export function utcDateKey(iso: string): string {
  return fromServerIso(iso).toUTC().toISODate() ?? '';
}

/**
 * The UTC time of day of an instant as fractional hours (0 ≤ h < 24) — for placing it on a
 * 24-hour dial. NaN-safe: invalid input yields 0.
 * @example utcHourOfDay('2026-10-15T20:30:00Z') => 20.5
 */
export function utcHourOfDay(iso: string): number {
  const dt = fromServerIso(iso).toUTC();
  return dt.isValid ? dt.hour + dt.minute / 60 + dt.second / 3600 : 0;
}

/**
 * Whole minutes from `fromIso` until the next `hour`:00 UTC (a daily schedule's next tick).
 * @example minutesUntilUtcHour(3, '2026-10-15T20:04:00Z') => 416
 */
export function minutesUntilUtcHour(hour: number, fromIso: string): number {
  const from = fromServerIso(fromIso).toUTC();
  if (!from.isValid) return 0;
  let next = from.startOf('day').set({ hour });
  if (next <= from) next = next.plus({ days: 1 });
  return Math.ceil(next.diff(from, 'minutes').minutes);
}

/** A UTC clock time (`HH:mm`) for an instant, e.g. beside a schedule that runs on UTC. */
export function formatUtcClock(iso: string): string {
  const dt = fromServerIso(iso).toUTC();
  return dt.isValid ? dt.toFormat('HH:mm') : '';
}

/**
 * Return the start of day for the given ISO date string.
 */
export function startOfDay(iso: string): string {
  return DateTime.fromISO(iso).startOf('day').toISO() ?? iso;
}

/**
 * Format an ISO date string as a compact US-style date (e.g. "Apr 10, 26").
 */
export function formatDateShort(iso: string, locale = 'en-US'): string {
  return DateTime.fromISO(iso, { locale }).toFormat('LLL dd, yy');
}

/**
 * Compact US-style date + time without the year (e.g. "Apr 10, 3:22 PM") —
 * for tight metadata lines that need the clock time alongside the date.
 */
export function formatDateTimeShort(iso: string, locale = 'en-US'): string {
  return DateTime.fromISO(iso, { locale }).toFormat('LLL dd, h:mm a');
}

/**
 * `formatDateTimeShort` for zone-less server timestamps that are really UTC (run `startedAt`):
 * read as UTC, shown in the viewer's zone — so it matches `formatUtcNaiveTimeOfDay`.
 */
export function formatUtcNaiveDateTimeShort(iso: string, locale = 'en-US'): string {
  return fromServerIso(iso).setLocale(locale).toLocal().toFormat('LLL dd, h:mm a');
}

/**
 * Just the day ("Oct 14") of a zone-less server timestamp that is really UTC, in the viewer's
 * zone — for one-line summaries where the clock time is noise.
 */
export function formatUtcNaiveDateShort(iso: string, locale = 'en-US'): string {
  return fromServerIso(iso).setLocale(locale).toLocal().toFormat('LLL d');
}

/**
 * The day and month ("30 Oct") of a zone-less server timestamp that is really UTC, in the viewer's
 * zone — the same day its `formatUtcNaiveDateTime` tooltip names.
 */
export function formatUtcNaiveDayMonth(iso: string, locale = 'en-US'): string {
  return fromServerIso(iso).setLocale(locale).toLocal().toFormat('d LLL');
}

/**
 * Format an ISO date as a bare day-and-month label (e.g. "5 Aug"). Used for
 * suggested run names, where the year is noise.
 */
export function formatDayMonth(iso: string, locale = 'en-US'): string {
  return DateTime.fromISO(iso, { locale }).toFormat('d LLL');
}

/**
 * Format a JS Date object as a short human-readable date string.
 * @example formatJSDate(new Date('2025-01-15')) => 'Jan 15, 2025'
 */
export function formatJSDate(date: Date): string {
  return DateTime.fromJSDate(date).toLocaleString(DateTime.DATE_MED);
}

/**
 * Format an ISO timestamp as a compact "Apr 16, 3:22 PM" audit-row label.
 */
export function formatAuditTimestamp(iso: string): string {
  return DateTime.fromISO(iso, { locale: 'en-US' }).toFormat('LLL dd, h:mm a');
}

/**
 * Format an ISO timestamp as "04/16/26" for compact date-range triggers.
 * Uses the DateTime's own date parts (not browser getDate/getMonth), so the
 * output reflects the stored ISO value without silent local-TZ mismatches.
 */
export function formatDateCompact(iso: string): string {
  return DateTime.fromISO(iso, { locale: 'en-US' }).toFormat('MM/dd/yy');
}

/**
 * Converts a JS Date (e.g. from a date-picker) to the start of that calendar
 * day as a full ISO 8601 datetime string. Used for `fromDate` API filter params.
 */
export function jsDateToIsoDate(date: Date): string {
  return DateTime.fromJSDate(date).startOf('day').toISO() ?? date.toISOString();
}

/**
 * Date-only (`YYYY-MM-DD`) string from a JS Date (e.g. a date-picker value),
 * using the date's local calendar day. For backend fields that expect a bare
 * calendar date rather than a full timestamp (e.g. analytics-sync dateRanges).
 */
export function jsDateToIsoDateOnly(date: Date): string {
  return DateTime.fromJSDate(date).toISODate() ?? date.toISOString().slice(0, 10);
}

/**
 * Shifts an ISO date string by `days` (use negative to subtract) and returns
 * the start of that calendar day as a full ISO 8601 datetime string. Used to
 * compute `fromDate` bounds for preset date-range filters.
 */
export function shiftIsoDate(isoDate: string, days: number): string {
  return DateTime.fromISO(isoDate).plus({ days }).startOf('day').toISO() ?? isoDate;
}

/**
 * Returns the end of today as a full ISO 8601 datetime string. Used as the
 * `toDate` bound for preset date-range filters.
 */
export function endOfTodayISO(): string {
  return DateTime.now().endOf('day').toISO();
}

/**
 * Normalize a user-picked Date (midnight-local from the date picker) to the
 * start of that local day expressed as an ISO 8601 string. Keeps date-range
 * filters from shifting a day when a user east of UTC picks a date.
 */
export function startOfLocalDayISO(date: Date): string {
  return DateTime.fromJSDate(date).startOf('day').toISO() ?? date.toISOString();
}

/**
 * Companion to `startOfLocalDayISO` for the inclusive end of a day.
 * Returns the last moment of the same local calendar day as an ISO string.
 */
export function endOfLocalDayISO(date: Date): string {
  return DateTime.fromJSDate(date).endOf('day').toISO() ?? date.toISOString();
}

/**
 * Format a UTC instant in an explicit IANA zone. Built for scheduled
 * catalog-operations: the API returns UTC instants, but every time belonging to
 * a schedule is meant to be read in that schedule's own `timezone`, so the zone
 * has to travel with the value instead of defaulting to the viewer's.
 * @example formatInZone('2026-08-19T02:00:00Z', 'Asia/Kolkata') => '19 Aug 2026, 07:30'
 */
export function formatInZone(iso: string, timeZone: string, locale = 'en-GB'): string {
  const dt = DateTime.fromISO(iso, { locale, zone: timeZone });
  return dt.isValid ? dt.toFormat('dd LLL yyyy, HH:mm') : '—';
}

/** Same instant, clock time only — for rows that show the date separately. */
export function formatTimeInZone(iso: string, timeZone: string, locale = 'en-GB'): string {
  const dt = DateTime.fromISO(iso, { locale, zone: timeZone });
  return dt.isValid ? dt.toFormat('HH:mm') : '—';
}

/** Same instant, short day + month + clock time, no year — for a one-off schedule's summary.
 *  @example formatDayMonthTimeInZone('2026-10-28T13:00:00Z', 'America/New_York') => '28 Oct 09:00' */
export function formatDayMonthTimeInZone(iso: string, timeZone: string, locale = 'en-GB'): string {
  const dt = DateTime.fromISO(iso, { locale, zone: timeZone });
  return dt.isValid ? dt.toFormat('d LLL HH:mm') : '—';
}

/** The calendar day (`YYYY-MM-DD`) an instant falls on in `timeZone` — for grouping a feed of
 *  instants by the viewer's local day. '' for invalid input.
 *  @example formatDateKeyInZone('2026-11-02T23:30:00Z', 'Asia/Kolkata') => '2026-11-03' */
export function formatDateKeyInZone(iso: string, timeZone: string): string {
  const dt = DateTime.fromISO(iso, { zone: timeZone });
  return dt.isValid ? dt.toISODate() : '';
}

/** The day an instant falls on in `timeZone`, with its weekday — a day-group heading.
 *  @example formatWeekdayDateInZone('2026-11-03T07:00:00Z', 'UTC') => 'Tue 3 Nov 2026' */
export function formatWeekdayDateInZone(iso: string, timeZone: string, locale = 'en-GB'): string {
  const dt = DateTime.fromISO(iso, { locale, zone: timeZone });
  return dt.isValid ? dt.toFormat('ccc d LLL yyyy') : '—';
}

/** Same instant with its weekday — for a list of upcoming run times.
 *  @example formatWeekdayDateTimeInZone('2026-10-28T13:00:00Z', 'America/New_York') => 'Wed 28 Oct 2026, 09:00' */
export function formatWeekdayDateTimeInZone(
  iso: string,
  timeZone: string,
  locale = 'en-GB',
): string {
  const dt = DateTime.fromISO(iso, { locale, zone: timeZone });
  return dt.isValid ? dt.toFormat('ccc d LLL yyyy, HH:mm') : '—';
}

/**
 * The same instant on the viewer's own clock, when that clock differs from `timeZone`'s at that
 * moment — so a time shown in a schedule's zone (`17:00 UTC`) also reads as `22:30 GMT+5:30` for
 * someone in India. `null` when the two clocks agree. Adds the day when it falls on another one.
 * @example formatViewerTimeIfDifferent('2026-10-25T17:00:00Z', 'UTC') // in IST => '22:30 GMT+5:30'
 */
export function formatViewerTimeIfDifferent(
  iso: string,
  timeZone: string,
  locale = 'en-GB',
): string | null {
  const there = DateTime.fromISO(iso, { locale, zone: timeZone });
  const here = DateTime.fromISO(iso, { locale });
  if (!there.isValid || !here.isValid || there.offset === here.offset) return null;
  const sameDay = there.toISODate() === here.toISODate();
  return here.toFormat(sameDay ? 'HH:mm ZZZZ' : 'ccc d LLL, HH:mm ZZZZ');
}

/** Same instant with its weekday and the zone's offset AT that instant (DST-aware) — so a
 *  schedule's run time reads unambiguously next to its UTC equivalent.
 *  @example formatWeekdayDateTimeWithOffset('2026-11-08T20:30:00Z', 'Asia/Kolkata') => 'Mon 9 Nov 2026, 02:00 GMT+5:30' */
export function formatWeekdayDateTimeWithOffset(
  iso: string,
  timeZone: string,
  locale = 'en-GB',
): string {
  const dt = DateTime.fromISO(iso, { locale, zone: timeZone });
  return dt.isValid ? dt.toFormat('ccc d LLL yyyy, HH:mm ZZZZ') : '—';
}

/** A calendar rule evaluated in a zone: which minutes/hours fire, and on which days.
 *  `weekday` follows cron (0 = Sunday … 6 = Saturday). */
export interface ZonedCalendarRule {
  minutes: number[];
  hours: number[];
  matchesDay: (day: { dayOfMonth: number; month: number; weekday: number }) => boolean;
}

/**
 * Next `count` instants matching `rule` as wall-clock times in `timeZone`, strictly after
 * `from`. Walks days (not minutes) so a weekly/monthly rule resolves in a few hundred steps;
 * gives up after ~13 months so a never-matching rule (Feb 30) terminates with `[]`.
 * Wall-clock times skipped by a DST jump are shifted forward, as Luxon does.
 */
export function nextZonedCalendarRuns(
  rule: ZonedCalendarRule,
  timeZone: string,
  count: number,
  from: Date = new Date(),
): Date[] {
  const start = DateTime.fromJSDate(from).setZone(timeZone);
  if (!start.isValid) return [];
  const hours = [...rule.hours].sort((a, b) => a - b);
  const minutes = [...rule.minutes].sort((a, b) => a - b);
  const runs: Date[] = [];
  const seen = new Set<number>();
  const firstDay = start.startOf('day');

  for (let offset = 0; offset < 400 && runs.length < count; offset++) {
    const day = firstDay.plus({ days: offset });
    // Luxon weekdays are 1 (Mon) … 7 (Sun); cron's are 0 (Sun) … 6 (Sat).
    if (!rule.matchesDay({ dayOfMonth: day.day, month: day.month, weekday: day.weekday % 7 })) {
      continue;
    }
    for (const hour of hours) {
      for (const minute of minutes) {
        const at = day.set({ hour, minute, second: 0, millisecond: 0 });
        // A time skipped by spring-forward is shifted onto the next hour, which can
        // collide with a real run — keep each instant once.
        if (at > start && runs.length < count && !seen.has(at.toMillis())) {
          seen.add(at.toMillis());
          runs.push(at.toJSDate());
        }
      }
    }
  }
  return runs;
}

/** True when the string is an IANA zone Luxon can resolve. Guards the timezone
 *  control before the API rejects an unknown zone with a 400. */
export function isValidTimeZone(timeZone: string): boolean {
  return DateTime.now().setZone(timeZone).isValid;
}

/** Current UTC offset of a zone, as a label for a timezone picker
 *  (e.g. `GMT+5:30`). Reflects DST as of now, which is what a picker wants. */
export function getZoneOffsetLabel(timeZone: string): string {
  const dt = DateTime.now().setZone(timeZone);
  return dt.isValid ? dt.toFormat('ZZZZ') : '';
}

/**
 * Browsers name some zones by their pre-rename IANA aliases (ICU's ids: `Asia/Calcutta`,
 * `Europe/Kiev`), while slim server images only carry the current names — so a zone picked
 * from `Intl` is sent under its current name. Keep in step with the API's alias map
 * (int-delivery `export_schedule_cadence.LEGACY_TIME_ZONES`).
 */
const LEGACY_TIME_ZONES: Readonly<Record<string, string>> = {
  'America/Buenos_Aires': 'America/Argentina/Buenos_Aires',
  'America/Catamarca': 'America/Argentina/Catamarca',
  'America/Cordoba': 'America/Argentina/Cordoba',
  'America/Godthab': 'America/Nuuk',
  'America/Indianapolis': 'America/Indiana/Indianapolis',
  'America/Jujuy': 'America/Argentina/Jujuy',
  'America/Louisville': 'America/Kentucky/Louisville',
  'America/Mendoza': 'America/Argentina/Mendoza',
  'Asia/Calcutta': 'Asia/Kolkata',
  'Asia/Katmandu': 'Asia/Kathmandu',
  'Asia/Rangoon': 'Asia/Yangon',
  'Asia/Saigon': 'Asia/Ho_Chi_Minh',
  'Atlantic/Faeroe': 'Atlantic/Faroe',
  'Europe/Kiev': 'Europe/Kyiv',
  'Pacific/Enderbury': 'Pacific/Kanton',
  'Pacific/Ponape': 'Pacific/Pohnpei',
  'Pacific/Truk': 'Pacific/Chuuk',
};

/** A zone under its current IANA name (`Asia/Calcutta` → `Asia/Kolkata`). */
export function canonicalTimeZone(timeZone: string): string {
  return LEGACY_TIME_ZONES[timeZone] ?? timeZone;
}

/** The viewer's own IANA zone, under its current name — the sensible default for a new schedule. */
export function getLocalTimeZone(): string {
  return canonicalTimeZone(DateTime.now().zoneName);
}

/** Every IANA zone the runtime can enumerate — the option pool for a searchable
 *  timezone picker. Returns `[]` on engines without `Intl.supportedValuesOf`, so
 *  callers keep a curated fallback list. */
export function getAllTimeZones(): string[] {
  const intl = Intl as typeof Intl & { supportedValuesOf?: (key: string) => string[] };
  if (typeof intl.supportedValuesOf !== 'function') return [];
  // Under current names, once each (ICU lists `Asia/Calcutta`, never `Asia/Kolkata`).
  return [...new Set(intl.supportedValuesOf('timeZone').map(canonicalTimeZone))];
}

/**
 * Interpret an `<input type="datetime-local">` value (`YYYY-MM-DDTHH:mm`, no
 * offset) as a wall-clock time in `timeZone` and return the UTC instant. A
 * schedule's start/end bounds are picked against the schedule's own zone, so
 * reading them in the viewer's zone would shift them.
 */
export function localDateTimeToISO(localValue: string, timeZone: string): string | null {
  const dt = DateTime.fromISO(localValue, { zone: timeZone });
  return dt.isValid ? dt.toUTC().toISO() : null;
}

/** Inverse of `localDateTimeToISO` — for seeding an edit form from stored ISO. */
export function isoToLocalDateTime(iso: string, timeZone: string): string {
  const dt = DateTime.fromISO(iso, { zone: timeZone });
  return dt.isValid ? dt.toFormat("yyyy-LL-dd'T'HH:mm") : '';
}

/** Date group labels used by the notifications inbox page. */
export type NotificationDateGroup = 'TODAY' | 'YESTERDAY' | 'EARLIER THIS WEEK' | 'EARLIER';

/**
 * Classifies an ISO timestamp into a notification date group relative to now.
 * Uses Luxon's startOf('day') for DST-safe day boundaries.
 */
export function getNotificationDateGroup(iso: string): NotificationDateGroup {
  const dt = DateTime.fromISO(iso);
  const now = DateTime.now();
  if (dt.hasSame(now, 'day')) return 'TODAY';
  if (dt.hasSame(now.minus({ days: 1 }), 'day')) return 'YESTERDAY';
  if (now.diff(dt, 'days').days < 7) return 'EARLIER THIS WEEK';
  return 'EARLIER';
}

/**
 * Formats a notification timestamp relative to now:
 * - Same day: relative ("2 min ago")
 * - Yesterday: "Yesterday, h:mm a"
 * - This week: "EEE, h:mm a" (Mon, 5:20 PM)
 * - Older: full date
 */
export function formatNotificationTimestamp(iso: string): string {
  const dt = DateTime.fromISO(iso);
  const now = DateTime.now();
  if (dt.hasSame(now, 'day')) return dt.toRelative() ?? iso;
  if (dt.hasSame(now.minus({ days: 1 }), 'day')) {
    return `Yesterday, ${dt.toFormat('h:mm a')}`;
  }
  if (now.diff(dt, 'days').days < 7) {
    return dt.toFormat('EEE, h:mm a');
  }
  return dt.toFormat('MMM d, h:mm a');
}

/**
 * Computes a human-readable processing duration between two ISO timestamps.
 * When `completedAt` is null (still processing), measures against the
 * current time instead. Returns '-' when `createdAt`/`completedAt` is
 * invalid. Granularity: days ≥ 1 d, hours ≥ 1 h, otherwise minutes.
 */
export function formatProcessingDuration(
  createdAt: string | null,
  completedAt: string | null,
): string {
  const start = DateTime.fromISO(createdAt ?? '');
  const end = completedAt ? DateTime.fromISO(completedAt) : DateTime.now();
  if (!start.isValid || !end.isValid) return '-';
  const totalMinutes = Math.round(end.diff(start, 'minutes').minutes);
  if (totalMinutes < 0) return '-';
  const totalHours = totalMinutes / 60;
  const totalDays = totalHours / 24;
  if (totalDays >= 1) {
    const d = Math.round(totalDays);
    return `${String(d)} ${d === 1 ? 'Day' : 'Days'}`;
  }
  if (totalHours >= 1) {
    const h = Math.round(totalHours);
    return `${String(h)} ${h === 1 ? 'Hour' : 'Hours'}`;
  }
  const m = Math.max(1, totalMinutes);
  return `${String(m)} ${m === 1 ? 'Minute' : 'Minutes'}`;
}

/**
 * Normalises any ISO instant to UTC with a `Z` suffix — for backends that parse query params as
 * `java.time.Instant`, which is strict about offsets.
 */
export function toUtcISO(iso: string): string {
  return DateTime.fromISO(iso).toUTC().toISO() ?? iso;
}

/**
 * Epoch milliseconds of a server timestamp; a zone-less one (`2026-11-03T15:42:00`) is UTC, one
 * with `Z` or an offset (`+05:30`, `-05:00`) keeps it. `null` for a missing or unparseable value.
 */
export function parseServerUtcMs(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const at = Date.parse(/[zZ]$|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`);
  return Number.isNaN(at) ? null : at;
}
